/**
 * JavaScript for Theme setting.
 */

// import { Processor } from "postcss";

/**
 * window size
 * - CSS変数に viewport / header / nav / CTA 等の高さを流し込む
 */
export function window_size() {
  function measureAndSet() {
    const windowWidth = document.documentElement.clientWidth;
    const scrollbarWidth = window.innerWidth - windowWidth;

    const headerEl = document.getElementById('site-header');
    const gnavEl   = document.getElementById('header-global-nav');
    const ctaEl    = document.getElementById('cta');
    const emeEl    = document.getElementById('emergency-head');

    const headerHeight = headerEl?.clientHeight ?? 0;
    const gNaviHeight  = gnavEl?.clientHeight ?? 0;
    const ctaHeight    = ctaEl?.clientHeight ?? 0;
    const emergencyHeadHeight = emeEl?.clientHeight ?? 0;

    const root = document.documentElement.style;
    root.setProperty('--window-width', String(windowWidth));
    root.setProperty('--window-width-px', `${windowWidth}px`);
    root.setProperty('--scrollbar-width-px', `${scrollbarWidth}px`);
    root.setProperty('--header-height-px', `${headerHeight}px`);

    // “存在する時だけ” をやめて、0でも入れておく方がCSSが安定します
    root.setProperty('--global-navi-height-px', `${gNaviHeight}px`);
    root.setProperty('--cta-px', `${ctaHeight}px`);
    root.setProperty('--emergency-head-px', `${emergencyHeadHeight}px`);
  }

  // 初回
  window.addEventListener('DOMContentLoaded', measureAndSet);

  // resize を rAF で間引き
  let ticking = false;
  window.addEventListener('resize', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      measureAndSet();
      ticking = false;
    });
  });
}

/**
 * In-view animator
 * - classが "animate-..." を含む要素に、画面進入時に "is-animated" を付与
 * - 一度付与したら監視解除（パフォーマンス）
 * - 動的に追加された要素も捕捉（MutationObserver）
 */
export function inview_animate() {
  window.addEventListener('DOMContentLoaded', () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    var selectorPrefix = 'animate-';
    var revealedClass = 'is-animated';
    var selector = '[class^="' + selectorPrefix + '"], [class*=" ' + selectorPrefix + '"]';

    var prefersReduced = false;
    try {
      prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {}

    function hasAnimatePrefix(el) {
      if (!el || !el.classList) return false;
      var arr = Array.prototype.slice.call(el.classList);
      return arr.some(function (c) { return c.indexOf(selectorPrefix) === 0; });
    }

    function collect(root) {
      var ctx = root || document;
      var list = ctx.querySelectorAll(selector);
      var arr = Array.prototype.slice.call(list);
      return arr.filter(function (el) { return !el.classList.contains(revealedClass); });
    }

    // ===== ブレークポイント定義 =====
    var mqMobile  = window.matchMedia('(max-width: 599.98px)');
    var mqTablet  = window.matchMedia('(min-width: 600px) and (max-width: 1047.98px)');
    var mqDesktop = window.matchMedia('(min-width: 1048px)');

    function getIOOptions() {
      // モバイル：手前で素早く付与（先読み強め）
      if (mqMobile.matches) {
        return { threshold: 0,    rootMargin: '0px 0px 10px 0px' };
      }
      // タブレット：中庸
      if (mqTablet.matches) {
        return { threshold: 0.05, rootMargin: '0px 0px 10px 0px' };
      }
      // デスクトップ：可視領域寄りで付与
      return { threshold: 0.10,   rootMargin: '0px 0px 10px 0px' };
    }

    // 低刺激 or IO非対応 → 即付与して終了
    if (prefersReduced || !('IntersectionObserver' in window)) {
      collect().forEach(function (el) { el.classList.add(revealedClass); });
      return;
    }

    var io = null;

    function buildObserver() {
      // 既存IOがあれば破棄
      if (io) io.disconnect();

      var opts = getIOOptions();
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add(revealedClass);
            io.unobserve(e.target);
          }
        });
      }, {
        root: null,
        threshold: opts.threshold,
        rootMargin: opts.rootMargin
      });

      // まだ表示前の要素を監視開始
      collect().forEach(function (el) { io.observe(el); });
    }

    // 初期IO作成
    buildObserver();

    // ===== 画面幅が変わったらIOを作り直す =====
    [mqMobile, mqTablet, mqDesktop].forEach(function (mq) {
      // modern: change / legacy: addListener
      if ('addEventListener' in mq) {
        mq.addEventListener('change', buildObserver);
      } else if ('addListener' in mq) {
        mq.addListener(buildObserver);
      }
    });

    // 動的追加（HTMX/SPA/Ajax等）
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.addedNodes) {
          Array.prototype.forEach.call(m.addedNodes, function (node) {
            if (!(node instanceof Element)) return;
            if (hasAnimatePrefix(node) && !node.classList.contains(revealedClass)) {
              io.observe(node);
            }
            collect(node).forEach(function (el) { io.observe(el); });
          });
        }
        if (
          m.type === 'attributes' &&
          m.target instanceof Element &&
          m.attributeName === 'class' &&
          hasAnimatePrefix(m.target) &&
          !m.target.classList.contains(revealedClass)
        ) {
          io.observe(m.target);
        }
      });
    });
    mo.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });

    // （任意）HTMX使用時：swap後に再スキャン
    if (document.body && document.body.addEventListener) {
      document.body.addEventListener('htmx:afterSwap', function (ev) {
        var target = ev && ev.target;
        if (target) collect(target).forEach(function (el) { io.observe(el); });
      });
    }
  });
}


export function inpage_smooth_scroll() {
  document.addEventListener('DOMContentLoaded', () => {
    const targetClasses = [
      'is-style-list-arrow-down-waku-2-2-1',
      'is-style-list-arrow-down-waku-3-2-1',
      'is-style-list-arrow-down-waku-4-3-2-1',
    ];

    const hasTargetBlock = targetClasses.some(className =>
      document.querySelector(`.${className}`)
    );

    if (hasTargetBlock) {
      document.documentElement.classList.add('has-smooth-scroll');
    }
  });
}
